import type { AuthApi } from './types';

export const useAuthApi = (): AuthApi => {
  const userStore = useUserStore();

  const { clearTokenCookies, setTokenCookies, setCurrencyCookies } = useAuthHelpers();

  const getAuthenticatedUser = async (useStore = true) => {
    try {
      const { data } = await useFetch('/api/auth/get-authenticated-user');
      if (useStore && data) {
        userStore.login({
          user: data.value.user,
          account: data.value.account,
          accounts: data.value.accounts,
        });
      }
      return data.value;
    } catch (error) {
      console.error('Failed to get authenticated user:', error);
      return null;
    }
  };

  const login = async (
    { email, password, withUserData = true, withAccountData = true }: LoginRequest,
    useStore = true
  ): Promise<boolean | undefined> => {
    const userStore = useUserStore();
    const runtimeConfig = useRuntimeConfig();
    await logout();

    try {
      const params = new URLSearchParams();
      if (withUserData !== undefined) {
        params.append('withUserData', String(withUserData));
      }
      if (withAccountData !== undefined) {
        params.append('withAccountData', String(withAccountData));
      }

      const url = `/api/auth/login?${params.toString()}`;

      // Make API call to login endpoint
      const { data, error } = await useFetch<LoginResponse>(url, {
        method: 'POST',
        body: {
          email: email,
          password: password,
        },
      });

      if (error.value) {
        throw new Error(error.value.data.message || 'Login failed.');
      }

      const response: LoginResponse | null = data.value;
      if (response?.success) {
        // Set token in cookies
        setTokenCookies(response.data.token);
        setCurrencyCookies(runtimeConfig.public.currency);

        // Update user store if necessary
        if (useStore) {
          userStore.login({
            user: response.data.userData ? response.data.userData.user : response.user,
            account:
              response.data.accountData && response.data.userData.accounts.length == 1
                ? response.data.accountData
                : {},
            accounts: response.data.userData ? response.data.userData.accounts : [],
            currency: runtimeConfig.public.currency,
          });
        }

        // Redirect user after successful login
        return response.success;
      }
    } catch (error) {
      console.error('Failed to login:', error);
      throw new Error((error as string) || 'Login failed.');
    }
  };

  const logout = async () => {
    try {
      // à ajouter API call to logout endpoint
      clearTokenCookies();
      userStore.logout();
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const sendResetPasswordEmail = async (email: string): Promise<SendResetPasswordEmailResponse> => {
    try {
      const { data, error } = await useFetch<SendResetPasswordEmailResponse>(
        '/api/auth/send-reset-password-email',
        {
          method: 'POST',
          body: { email },
        }
      );

      if (error.value) throw new Error(error.value.message);

      return data.value
        ? data.value
        : { success: false, message: 'Failed to send reset password email.' };
    } catch (err) {
      return {
        success: false,
        message: err instanceof Error ? err.message : 'An error occurred',
      };
    }
  };

  const resetPassword = async (
    newPassword: string,
    resetPasswordToken: string
  ): Promise<ResetPasswordResponse> => {
    try {
      const { data, error } = await useFetch<ResetPasswordResponse>('/api/auth/reset-password', {
        method: 'POST',
        body: { newPassword, resetPasswordToken },
      });

      if (error.value) throw new Error(error.value.message);

      return data.value ? data.value : { success: false, message: 'Failed to reset password.' };
    } catch (err) {
      return {
        success: false,
        message: err instanceof Error ? err.message : 'An error occurred',
      };
    }
  };

  const getStores = async () => {
    try {
      const storyStore = useStoryblokStore();
      const story = await useAsyncStoryblok('stores', {
        version: 'published',
      });
      if (story?.value) {
        storyStore.setStory('stores', story.value?.content?.Stores);
      }
      return story.value?.content?.Stores;
    } catch (error) {
      console.error('Failed to get stores:', error);
      return null;
    }
  };
  function getExpiredDate(token: string): Date | null {
    try {
      const payloadBase64 = token.split('.')[1];
      const payloadJson = Buffer.from(payloadBase64, 'base64').toString('utf8');
      const payload = JSON.parse(payloadJson);
      if (payload.exp) {
        return new Date(payload.exp * 1000);
      }
      return null;
    } catch (e) {
      console.error('Error decoding JWT:', e);
      return null;
    }
  }

  const loginWithToken = async (token: string) => {
    try {
      const accessTokenExp = getExpiredDate(token);
      const accessTokenMaxAge = accessTokenExp
        ? Math.floor((accessTokenExp.getTime() - Date.now()) / 1000)
        : 60 * 60 * 2; // 2 heures par défaut

      // Configuration des cookies avec le token et l'état d'authentification
      const cookieOptions = {
        path: '/',
        maxAge: accessTokenMaxAge,
        httpOnly: false, // Permet l'accès côté client
        secure: process.env.NODE_ENV === 'production', // HTTPS en production
        sameSite: 'lax' as const,
      };

      // Définir le token dans les cookies
      const tokenCookie = useCookie('token', cookieOptions);
      tokenCookie.value = token;

      // Définir l'état d'authentification
      const isAuthenticatedCookie = useCookie('isAuthenticated', {
        ...cookieOptions,
        maxAge: accessTokenMaxAge,
      });
      isAuthenticatedCookie.value = 'true';

      // Mettre à jour le store utilisateur
      const userStore = useUserStore();
      userStore.isAuthenticated = true;

      console.log('Login with token success');
      console.log('Token cookie set:', !!tokenCookie.value);
      console.log('isAuthenticated cookie:', isAuthenticatedCookie.value);

      return true;
    } catch (error) {
      console.error('Failed to login with token:', error);

      // En cas d'erreur, s'assurer que les cookies d'authentification sont nettoyés
      const tokenCookie = useCookie('token');
      const isAuthenticatedCookie = useCookie('isAuthenticated');

      tokenCookie.value = null;
      isAuthenticatedCookie.value = 'false';

      return false;
    }
  };

  return {
    getAuthenticatedUser,
    login,
    logout,
    sendResetPasswordEmail,
    resetPassword,
    getStores,
    loginWithToken,
  };
};
